import React, { useMemo, useState } from 'react';
import Button from '../../../../../../generic-components/Button';
import Heading from '../../../../../../generic-components/Heading';
import { convertValueToUSD } from '../../../../../../helpers/currency.helper';
import { UserMessage } from '../../../../../../DesignSystem/LiliUserMessage/UserMessage';
import { DISCOUNT_TYPES } from './Discount';
import { EditSalesTaxForm } from './EditSalesTaxForm';
import { useStore } from '../../../../../../store';

interface IProps {
  salesTax?: number;
  discountType?: string;
  discountAmount?: number;
  amount?: number;
}

export const TaxRate = ({ salesTax, amount, discountAmount, discountType }: IProps) => {
  const [isEditTaxRate, setIsEditTaxRate] = useState<boolean>(false);
  const invoiceData = useStore((state) => state.invoiceData);
  const setInvoiceDataToUpdate = useStore((state) => state.setInvoiceDataToUpdate);

  const onRemoveTax = () => {
    if (invoiceData) {
      const invoiceDataToUpdate = { ...invoiceData };
      if (invoiceDataToUpdate?.invoiceService && 'salesTax' in invoiceDataToUpdate.invoiceService) {
        invoiceDataToUpdate.invoiceService.salesTax = null;
        setInvoiceDataToUpdate(invoiceDataToUpdate);
      }
    }
    setIsEditTaxRate(false);
  };

  const discountValue = useMemo(() => {
    if (!amount || !discountType || !discountAmount) return 0;
    if (discountType === DISCOUNT_TYPES.PERCENT) {
      return discountAmount * amount;
    }
    if (discountType === DISCOUNT_TYPES.FIXED) {
      return discountAmount;
    }
    return 0;
  }, [amount, discountAmount, discountType]);

  const salesTaxSubtitle = useMemo(() => {
    if (!salesTax) return '';
    return `${(salesTax * 100).toFixed(2)}% on ${convertValueToUSD((amount || 0) - discountValue, 2)}`;
  }, [salesTax, amount, discountValue]);

  const taxValue = useMemo(() => {
    if (!amount || !salesTax) return 0;
    return salesTax * (amount - discountValue);
  }, [salesTax, amount, discountValue, invoiceData]);
  return !salesTax ? (
    <div className='flex items-center justify-between grow py-[18px] px-[16px] border-b border-bw03'>
      <Button
        onClick={() => setIsEditTaxRate(true)}
        className=''
        id=''
        childElement={
          <Heading title='Add sales tax' className='font-metric text-[14px] text-action03 hover:underline' />
        }
      />
      <Heading title={convertValueToUSD(0, 2)} className='font-metric text-[14px] text-bw08' />
      {isEditTaxRate && (
        <UserMessage
          mainWrapperClassName='backdrop-blur-sm'
          contentWrapperClassName='animate-fadeIn'
          onClick={() => setIsEditTaxRate(false)}
          id='clientEditFormWrapper'
        >
          <div className='w-[568px] min-h-[100px] bg-bw00 rounded-[12px]'>
            <EditSalesTaxForm onClose={() => setIsEditTaxRate(false)} />
          </div>
        </UserMessage>
      )}
    </div>
  ) : (
    <div className='flex items-center justify-between grow py-[18px] px-[16px] border-b border-bw03'>
      <div className='flex flex-col'>
        <Heading title='Sales tax' className='font-metric text-[14px] text-bw08' />
        <Heading title={salesTaxSubtitle} className='font-metric text-[12px] text-bw06' />
      </div>
      <div className='flex flex-col items-end'>
        <Heading
          title={`${taxValue > 0 ? '+' : ''}${convertValueToUSD(taxValue, 2)}`}
          className='font-metric text-[14px] text-red02'
        />
        <Button
          onClick={onRemoveTax}
          className=''
          id=''
          childElement={
            <Heading title='Remove' className='text-action03 font-metric text-[14px] underline hover:text-action02' />
          }
        />
      </div>
    </div>
  );
};
